import type { ThemeTokens } from 'styled-components';

export const FONT_FAMILY = "'Instrument Sans', sans-serif";

const heatherGray = {
	'50': '#faf5f9',
	'100': '#f0eef3',
	'200': '#e4dfe9',
	'300': '#c8c2d0',
	'400': '#9e93a9',
	'500': '#897c9a',
	'600': '#543c6c',
	'700': '#452c5a',
	'800': '#311549',
	'900': '#1a092c',
};

const vividPink = {
	'50': '#fff0ff',
	'100': '#ffe4ff',
	'200': '#ffbfff',
	'300': '#ff9eff',
	'400': '#ff7aff',
	'500': '#e940e0',
	'600': '#a600a0',
	'700': '#760074',
	'800': '#4f0050',
	'900': '#340035',
};

const softBeige = {
	'100': '#fcfbfa',
	'200': '#faf5f2',
	'500': '#ede8e4',
	'600': '#e2dbd5',
};

const vibrantPeach = {
	'50': '#fff8f0',
	'100': '#ffefde',
	'200': '#ffdec0',
	'300': '#ffd0a5',
	'400': '#ffc089',
	'500': '#f6953c',
	'600': '#c26a12',
	'700': '#8e4d0c',
	'800': '#613407',
	'900': '#371d04',
};

const electricBlue = {
	'50': '#f2fcff',
	'100': '#e0f9ff',
	'200': '#b5f4ff',
	'300': '#93eafb',
	'400': '#72ddf7',
	'500': '#2cb7e0',
	'600': '#1786ac',
	'700': '#10647f',
	'800': '#0b4356',
	'900': '#082733',
};

const freshGreen = {
	'50': '#edfbf4',
	'100': '#d7f6e7',
	'200': '#afedcf',
	'300': '#7fe0b2',
	'400': '#4fd194',
	'500': '#2ebd7e',
	'600': '#1f9663',
	'700': '#177250',
	'800': '#124d38',
	'900': '#122a20',
};

const coralRed = {
	'50': '#fdeeef',
	'100': '#fbd9db',
	'200': '#f6b1b5',
	'300': '#f0868c',
	'400': '#ea5f67',
	'500': '#e23c46',
	'600': '#b82b35',
	'700': '#8c2029',
	'800': '#60161d',
	'900': '#331014',
};

const white = '#ffffff';

export const palettes = {
	heatherGray,
	vividPink,
	softBeige,
	vibrantPeach,
	electricBlue,
	freshGreen,
	coralRed,
	white,
};

export const space = {
	'3xs': 2,
	'2xs': 4,
	xs: 8,
	sm: 12,
	md: 16,
	lg: 24,
	xl: 32,
	'2xl': 48,
	'3xl': 64,
};

/** Max width of conversation and feedback page content. */
export const PAGE_CONTENT_WIDTH = 1000;

const buttonShadow = '0 1px 1px rgba(20, 16, 28, 0.04), 0 3px 6px rgba(20, 16, 28, 0.02)';
const insetShadow = 'inset 0 2px 2px rgba(26, 9, 44, 0.05)';

export const tokens: ThemeTokens = {
	text: {
		primary: heatherGray['900'],
		secondary: heatherGray['600'],
		tertiary: heatherGray['400'],
		disabled: heatherGray['300'],
		inverse: white,
		link: vividPink['600'],
		white,
	},
	surface: {
		sidebar: heatherGray['900'],
		primary: softBeige['100'],
		secondary: softBeige['200'],
		hover: softBeige['100'],
		selected: vividPink['50'],
		white,
		skeleton: softBeige['200'],
		overlay: '#1a092c80',
	},
	border: {
		subtle: softBeige['600'],
		default: heatherGray['300'],
		strong: heatherGray['400'],
		input: heatherGray['300'],
		focus: heatherGray['900'],
		white,
	},
	icon: {
		primary: heatherGray['900'],
		secondary: heatherGray['600'],
		tertiary: heatherGray['500'],
	},
	brand: {
		text: vividPink['600'],
		background: vividPink['50'],
		hover: vividPink['200'],
		border: vividPink['400'],
		iconOnBg: vividPink['600'],
		icon: vividPink['500'],
	},
	info: {
		text: electricBlue['700'],
		background: electricBlue['50'],
		hover: electricBlue['300'],
		border: electricBlue['400'],
		iconOnBg: electricBlue['700'],
		icon: electricBlue['500'],
	},
	success: {
		text: freshGreen['700'],
		background: freshGreen['50'],
		hover: white,
		border: freshGreen['400'],
		iconOnBg: freshGreen['700'],
		icon: freshGreen['500'],
	},
	warning: {
		text: vibrantPeach['700'],
		background: vibrantPeach['50'],
		hover: white,
		border: vibrantPeach['400'],
		iconOnBg: vibrantPeach['700'],
		icon: vibrantPeach['500'],
	},
	error: {
		text: coralRed['600'],
		background: coralRed['50'],
		hover: coralRed['200'],
		border: coralRed['400'],
		iconOnBg: coralRed['600'],
		icon: coralRed['500'],
	},
	radius: {
		xs: 4,
		sm: 8,
		md: 12,
		lg: 16,
		xl: 24,
		full: 999,
	},
	padding: {
		compact: space.xs,
		control: space.sm,
		container: space.md,
		section: space.lg,
		page: space.xl,
	},
	gap: {
		inline: space['2xs'],
		element: space.xs,
		list: space.sm,
		group: space.md,
		section: space.xl,
		page: space['2xl'],
	},
	shadow: {
		button: buttonShadow,
		xs: '0 2px 4px rgba(20,16,28,0.03), 0 1px 2px rgba(20,16,28,0.06)',
		md: '0 4px 12px -2px rgba(26,9,44,0.12), 0 2px 4px rgba(20,16,28,0.06)',
		lg: '0 4px 8px rgba(20,16,28,0.08), 0 12px 32px -4px rgba(20,16,28,0.16)',
		inset: insetShadow,
		input: `${buttonShadow}, ${insetShadow}`,
	},
	typography: {
		fontFamily: FONT_FAMILY,
		sizes: { small: 13, body: 14, label: 14, h4: 14, h3: 16, h2: 18, h1: 24, title: 32 },
		weights: { regular: 450, medium: 550, semibold: 600, bold: 700 },
		lineHeights: { heading: 1.25, body: 1.6, small: 1.5 },
	},
};
